import { IDTEL_DATA } from "../data/idtelData";
import {
  IDTEL_STEPS_SCHEMA,
  IDTEL_DEFAULT_ANSWER,
  IDTEL_OMITTED_ANSWER,
} from "../data/idtelSchema";

/** Maps toolbar age (6–9) to step-0 `Rango_Etario`. */
export function mapAgeToRangoEtario(age) {
  const n = parseInt(age, 10);
  if (n >= 8) return "b";
  return "a";
}

/**
 * Inversa parcial de mapAgeToRangoEtario, para editar una corrección previa.
 * El paso 0 solo persiste el tramo ("a" = 6–7, "b" = 8–9), no la edad exacta,
 * así que se recupera la primera edad del tramo. No altera el resultado: la
 * edad solo llega a RedPsi convertida de vuelta a este mismo tramo.
 */
export function rangoEtarioToAge(rango) {
  return rango === "b" ? "8" : "6";
}

export function idtelDomainKey(mIdx, dIdx) {
  return "m" + mIdx + "d" + dIdx;
}

/** Flat list of domain descriptors in API step order (15 steps). */
export function getIdtelDomainSteps() {
  const steps = [];
  IDTEL_DATA.forEach((micro, mIdx) => {
    micro.domains.forEach((domain, dIdx) => {
      steps.push({
        mIdx,
        dIdx,
        key: idtelDomainKey(mIdx, dIdx),
        micro: micro.micro,
        name: domain.name,
        count: domain.count,
        stepIndex: steps.length,
      });
    });
  });
  return steps;
}

/**
 * Alternativas de un ítem, más la opción "Omitida" al final.
 *
 * Las del schema son las del protocolo: exactamente una vale
 * `IDTEL_DEFAULT_ANSWER` ("1", la correcta) y el resto son distractores, que
 * RedPsi codifica con "0", "00" y "000" según el tipo de error. Los tres son
 * respuestas incorrectas: la UI no debe distinguirlos.
 *
 * "Omitida" no es una alternativa del protocolo — es el ítem que no se aplicó —
 * así que se agrega acá y no en el schema.
 */
export function getIdtelItemOptions(stepIndex, rowIdx) {
  const step = IDTEL_STEPS_SCHEMA[stepIndex];
  if (!step) return [];
  const item = step.find((entry) => entry.var === "p" + (rowIdx + 1));
  if (!item) return [];
  return [
    ...item.options,
    { value: IDTEL_OMITTED_ANSWER, text: "Omitida" },
  ];
}

export function createIdtelUiState() {
  const state = {};
  getIdtelDomainSteps().forEach((step) => {
    state[step.key] = Array(step.count).fill(IDTEL_DEFAULT_ANSWER);
  });
  return state;
}

export function buildIdtelStepsAnswers(uiState, age) {
  const steps = getIdtelDomainSteps();
  return steps.map((step, stepIndex) => {
    const answers = {};
    const values = uiState[step.key] || [];

    if (stepIndex === 0) {
      answers.Rango_Etario = mapAgeToRangoEtario(age);
    }

    values.forEach((apiVal, idx) => {
      answers["p" + (idx + 1)] = apiVal || IDTEL_DEFAULT_ANSWER;
    });

    return answers;
  });
}

/**
 * ¿El guardado tiene los 15 pasos? Las correcciones hechas antes de que
 * runIdtelCorrection encadenara `previous_answer` solo persistieron el último
 * dominio, y rehidratar esas dejaría los otros 14 en "todo correcto" — o sea,
 * re-corregir daría un puntaje inventado. Esas no se pueden editar.
 */
export function hasCompleteIdtelAnswers(stored) {
  if (!stored) return false;
  return getIdtelDomainSteps().every(
    (step, stepIndex) => stored["step-" + stepIndex]
  );
}

/**
 * Inversa de buildIdtelStepsAnswers: reconstruye el uiState de los 15 pasos
 * desde lo que se guardó (`{'step-N': {p1, p2, …}}`). Los ítems que no vengan
 * en el guardado quedan en el default (respuesta correcta).
 */
export function parseIdtelStepsAnswers(stored) {
  const state = createIdtelUiState();
  if (!stored) return state;
  getIdtelDomainSteps().forEach((step, stepIndex) => {
    const stepAnswers = stored["step-" + stepIndex];
    if (!stepAnswers) return;
    state[step.key] = state[step.key].map((fallback, idx) => {
      const value = stepAnswers["p" + (idx + 1)];
      return value == null || value === "" ? fallback : String(value);
    });
  });
  return state;
}

/**
 * Conteo de los 185 ítems en tres cubetas que sí suman el total.
 *
 * Solo hay tres estados posibles: la alternativa correcta ("1"), la omitida
 * ("om") y todo lo demás, que son los distractores del protocolo. Contar los
 * distractores por descarte —en vez de enumerar "0"/"00"/"000"— es lo que
 * garantiza que `correct + incorrect + omitted === total`: antes se enumeraban
 * y los ítems con "000" (12, todos del paso de denominación) no caían en
 * ninguna cubeta y desaparecían de la cuenta.
 */
export function summarizeIdtelState(uiState) {
  let total = 0;
  let correct = 0;
  let incorrect = 0;
  let omitted = 0;

  Object.keys(uiState).forEach((key) => {
    (uiState[key] || []).forEach((val) => {
      total += 1;
      if (val === IDTEL_DEFAULT_ANSWER) correct += 1;
      else if (val === IDTEL_OMITTED_ANSWER) omitted += 1;
      else incorrect += 1;
    });
  });

  return { total, correct, incorrect, omitted };
}
